import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings } from "@/contexts/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Car, Eye, EyeOff, ShieldCheck, ArrowLeft, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const { login, register } = useAuth();
  const { settings } = useSettings();
  const [isRegister, setIsRegister] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isForgot) {
        await axios.post(`${API}/auth/forgot-password`, { email });
        setForgotSent(true);
        toast.success("Email di reset inviata!");
      } else if (isRegister) {
        await register(email, password, name, inviteCode);
        toast.success("Registrazione completata!");
      } else {
        await login(email, password);
        toast.success("Accesso effettuato!");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore di autenticazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-white dark:bg-slate-950">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden" style={{ backgroundColor: settings?.primary_color || "#DC2626" }}>
                {settings?.app_icon_url ? (
                  <img src={settings.app_icon_url} alt="Logo" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <Car className="w-5 h-5 text-white" />
                )}
              </div>
              <span className="text-xl font-bold tracking-tight font-outfit text-slate-900 dark:text-slate-100">
                {settings?.app_name || "Noleggio Veloce"}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight font-outfit text-slate-900 dark:text-slate-100">
              {isForgot ? "Password Dimenticata" : isRegister ? "Crea Account" : "Bentornato"}
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base">
              {isForgot
                ? "Inserisci la tua email per ricevere il link di reset"
                : isRegister
                ? "Registrati per gestire i tuoi noleggi"
                : "Accedi per gestire la tua flotta"}
            </p>
          </div>

          {isForgot && forgotSent ? (
            <div className="space-y-5 text-center py-6">
              <div className="w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-950 flex items-center justify-center mx-auto">
                <Mail className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Email Inviata!</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Controlla la tua casella di posta ({email}).<br/>
                Troverai un link per reimpostare la password.
              </p>
              <p className="text-xs text-slate-400">Controlla anche la cartella Spam</p>
              <Button onClick={() => { setIsForgot(false); setForgotSent(false); }} variant="outline" className="mt-4">
                <ArrowLeft className="w-4 h-4 mr-2" /> Torna al Login
              </Button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700 dark:text-slate-200 font-medium">Nome</Label>
                <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mario Rossi" className="h-11 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg" required />
              </div>
            )}
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="inviteCode" className="text-slate-700 dark:text-slate-200 font-medium flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5" style={{ color: settings?.primary_color || "#DC2626" }} />Codice Staff
                </Label>
                <Input id="inviteCode" data-testid="register-invite-code-input" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} placeholder="Inserisci il codice fornito dall'admin" className="h-11 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg" required />
                <p className="text-[11px] text-slate-400">Solo i membri dello staff possono registrarsi</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-200 font-medium">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario@esempio.it" className="h-11 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg" required />
            </div>
            {!isForgot && (
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 dark:text-slate-200 font-medium">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? "text" : "password"} data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="La tua password" className="h-11 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg pr-10" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" data-testid="toggle-password-visibility">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            )}
            <Button type="submit" data-testid="login-submit-button" disabled={submitting} style={{ backgroundColor: settings?.primary_color || "#DC2626" }} className="w-full h-11 hover:opacity-90 text-white font-medium rounded-lg shadow-sm transition-all">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isForgot ? "Invia Link di Reset" : isRegister ? "Registrati" : "Accedi"}
            </Button>
          </form>
          )}

          {!isForgot && !forgotSent && (
            <div className="text-center space-y-2">
              <button onClick={() => setIsRegister(!isRegister)} className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors block mx-auto" data-testid="toggle-auth-mode">
                {isRegister ? "Hai gia un account? Accedi" : "Non hai un account? Registrati"}
              </button>
              {!isRegister && (
                <button onClick={() => { setIsForgot(true); setForgotSent(false); }} className="text-sm text-blue-500 hover:text-blue-700 transition-colors block mx-auto" data-testid="forgot-password-link">
                  Password dimenticata?
                </button>
              )}
            </div>
          )}
          {isForgot && !forgotSent && (
            <div className="text-center">
              <button onClick={() => setIsForgot(false)} className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors" data-testid="back-to-login">
                <ArrowLeft className="w-3 h-3 inline mr-1" /> Torna al Login
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900">
          <img
            src={settings?.login_image_url || "https://images.unsplash.com/photo-1625869740698-983464d352ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxmaWF0JTIwNTAwJTIwdmludGFnZSUyMGl0YWx5JTIwc3RyZWV0fGVufDB8fHx8MTc3MzQ2OTY0Nnww&ixlib=rb-4.1.0&q=85"}
            alt="Login Background"
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
        </div>
        <div className="relative z-10 flex flex-col justify-end h-full p-12">
          <blockquote className="space-y-4">
            <p className="text-2xl font-outfit font-bold text-white leading-relaxed">
              "{settings?.login_quote || "Gestisci la tua flotta con stile e velocità."}"
            </p>
            <p className="text-slate-300 text-sm">
              {settings?.app_name || "Noleggio Veloce"} - {settings?.app_tagline || "Gestione Noleggi Senza Freni"}
            </p>
          </blockquote>
        </div>
      </div>
    </div>
  );
}
